import React, {useEffect, useMemo, useState} from "react";
import {StyledInput} from "./cell";
import Tooltip from "@mui/material/Tooltip";
import Dropdown from "./Dropdown";
import {useTranslation} from "react-i18next";
import {statusIcons} from "./index";
import Stack from "@mui/material/Stack";
import {preventValueChangeOnScroll} from "../../../utils/input";

const identity = (x) => x;

const EditableCell = ({
                          initialValue,
                          editedValue,
                          options,
                          multiple = false,
                          renderOptions = identity,
                          getOptionValue = identity,
                          renderValue = identity,
                          onUpdate, validationErrors = [],
                          validationWarnings = [],
                          validationRegistry = {},
                          dropdownWidth
                      }) => {
    const [value, setValue] = useState(editedValue ?? initialValue);

    const handleInput = (event) => {
        handleValueChange(event.target.value);
    }

    const handleValueChange = (value) => {
        setValue(value);
        if(onUpdate) {
            onUpdate(value);
        }
    }

    // Detect external changes, like when the user edit an entire column
    useEffect(() => {
        if (editedValue !== undefined && editedValue !== value) {
            setValue(editedValue);
        }
    }, [editedValue, value]);

    // Detect external change, when the initial data is updated (the user change the model in an external dialog,
    // then the data is fetched again and new fresh initialValue is passed)
    useEffect(() => {
        if (value === initialValue) return; // No need to update if the value is the same as the initial value
        if (editedValue !== undefined) return; // Don't override the value if the user has already edited it

        setValue(initialValue);
    }, [initialValue]);

    const changed = initialValue !== value;
    const warnings = !changed && validationWarnings.length > 0;
    const errors = !changed && validationErrors.length > 0;

    const message = [...validationErrors, ...validationWarnings].map(e => validationRegistry[e] ?? e).join(", ");

    if (options) {
        return <Dropdown value={value ?? ""}
                         renderOptions={renderOptions}
                         getOptionValue={getOptionValue}
                         renderValue={renderValue}
                         options={options}
                         multiple={multiple}
                         onSelect={handleValueChange}
                         changed={changed}
                         warning={warnings}
                         error={errors}
                         tooltip={message}
                         buttonWidth={dropdownWidth}
        />
    }

    return <Tooltip title={message} placement="top" disableInteractive>
        <StyledInput value={value ?? ""}
                     type="number"
                     changed={changed}
                     warning={warnings}
                     error={errors}
                     onWheel={preventValueChangeOnScroll}
                     onChange={handleInput}/>
    </Tooltip>
}

export const EditableParamCell = ({cell}) => {
    const {model1Registry, updates, updateData} = cell.table.options.meta ?? {};
    const rowId = cell.row?.original?.id;
    const columnId = cell.column.id;
    const options = model1Registry?.[columnId];
    const initialValue = cell.getValue()?.toString();
    const editedValue = updates?.[rowId]?.[columnId];
    const validationErrors = cell.row?.original?.validationErrors[`${cell.column.id}`] ?? [];
    const validationWarnings = cell.row?.original?.validationWarnings[`${cell.column.id}`] ?? [];
    const model1ValidationRegistry = cell.table.options?.meta.model1ValidationRegistry ?? {};

    const onUpdate = (value) => {
        updateData({id: rowId, [columnId]: value});
    }

    return <EditableCell initialValue={initialValue}
                         editedValue={editedValue}
                         options={options}
                         renderOptions={(option) => `${option.code} - ${option.observation}`}
                         getOptionValue={(option) => option.code}
                         onUpdate={onUpdate}
                         validationErrors={validationErrors}
                         validationWarnings={validationWarnings}
                         validationRegistry={model1ValidationRegistry}
    />
}

export const EditableStatusCell = ({cell, emptyValue=""}) => {
    const {t} = useTranslation("enums");
    const renderValue = (value) => (value ? statusIcons[value] : emptyValue)

    const {updates, updateData} = cell.table.options.meta ?? {};
    const rowId = cell.row?.original?.id;
    const columnId = cell.column.id;
    const options = ["OPEN", "CLOSED", "CONFIRMED"]
    const initialValue = cell.getValue()?.toString();
    const editedValue = updates?.[rowId]?.[columnId];

    const renderOptions = (value) => (<Stack direction="row" spacing={1}>
        {statusIcons[value]}
        <span>{t(`model1Status.${value}`)}</span>
    </Stack>
    )

    const onUpdate = (value) => {
        updateData({id: rowId, [columnId]: value});
    }

    return <EditableCell initialValue={initialValue}
                         editedValue={editedValue}
                         options={options}
                         renderOptions={renderOptions}
                         renderValue={renderValue}
                         onUpdate={onUpdate}
     />
}


const getHNAuxValues = (row) => {
    let values = []
    if (row.hnR) values.push("R");
    if (row.hnT) values.push("T");
    if (row.hnW) values.push("W");
    if (row.hnMm) values.push("Mm");
    if (row.hnMe) values.push("Me");
    if (row.hnG) values.push("G");
    return values;
}

const getHNAuxString = (values) => {
    return values.join(",");
}

export const EditableHNAux = ({cell, options}) => {
    const {updates, updateData} = cell.table.options.meta ?? {};
    const row = cell.row?.original ?? {};
    const editedRow = updates?.[row.id];
    const rowId = cell.row?.original?.id;

    const initialValue = useMemo(
        () => getHNAuxValues(cell.row.original),
        [row.hnR, row.hnT, row.hnW, row.hnMm, row.hnMe, row.hnG]
    );
    const editedValue =useMemo(
        () => !!editedRow ? getHNAuxValues(editedRow) : undefined,
        [editedRow?.hnR, editedRow?.hnT, editedRow?.hnW, editedRow?.hnMm, editedRow?.hnMe, editedRow?.hnG]
    );

    const onUpdate = (values) => {
        let flags = {
            hnR: values.includes("R"),
            hnT: values.includes("T"),
            hnW: values.includes("W"),
            hnMm: values.includes("Mm"),
            hnMe: values.includes("Me"),
            hnG: values.includes("G"),
        }
        updateData({id: rowId, ...flags});
    }

    return <EditableCell initialValue={initialValue}
                         editedValue={editedValue}
                         options={options}
                         multiple
                         renderValue={getHNAuxString}
                         renderOptions={(option) => option.label}
                         getOptionValue={(option) => option.value}
                         onUpdate={onUpdate}
                         dropdownWidth="3rem"
    />
}


export default EditableCell;